// Frame-timing telemetry ("perf beacon", v259). A tiny always-on frame monitor:
// one rAF callback that folds each frame interval into a per-scene histogram,
// plus a PerformanceObserver for long-animation-frame (or longtask) entries.
// Nothing is allocated per frame (a hitch >= 50 ms allocates one small record).
// It pauses with the tab (rAF stops; the interval clock resets on return).
//
// What it reports, per scene (title, pack, battle, campaign, codex, ...):
//   h   frame-time histogram, buckets <17 <25 <34 <50 <100 >=100 ms
//   hx  hitches (>= 50 ms), w the worst frame, n/ms frames and their total time (avg fps)
//   lt  long frames the browser attributed (LoAF / longtask), ltms their total
// plus the worst hitches of the chunk (time, scene, sub-phase), the longest LoAF
// script attributions, and coarse device context (GPU renderer string, DPR,
// viewport, quality tier, cores, memory, browser + OS family; no full UA, no IP).
//
// Sending: navigator.sendBeacon at the end of a match (deferred to idle), when
// the tab hides, and on pagehide. Never from inside the frame loop.
const URL_BEACON = '/api/nomadmon/perf-beacon';
const EDGES = [17, 25, 34, 50, 100];
const HITCH_MS = 50;
const MAX_WORST = 10, MAX_LOAF = 8, MAX_SCENES = 24;
const MIN_FRAMES = 60;          // a chunk shorter than ~1 s of frames is not worth a row

let app = 'v0';
let resolveScene = () => 'boot';
let resolveSub = () => null;
let quality = () => ({ step: 0, sheds: 0, restores: 0 });
let gpu = null;
const sid = (() => { try { const a = new Uint32Array(2); crypto.getRandomValues(a); return (a[0].toString(36) + a[1].toString(36)).slice(0, 12).padEnd(6, '0'); } catch { return Math.random().toString(36).slice(2, 12).padEnd(6, '0'); } })();
let seq = 0;

// ------------------------------------------------------------------ the chunk
let scenes = new Map();          // label -> record (created on first frame in that scene)
let worst = [];
let loafs = [];
let chunkT0 = performance.now();
let label = 'boot', rec = null;
function sceneRec(s) {
  let r = scenes.get(s);
  if (!r) {
    if (scenes.size >= MAX_SCENES) s = 'other';
    r = scenes.get(s);
    if (!r) { r = { s, n: 0, ms: 0, h: [0, 0, 0, 0, 0, 0], hx: 0, w: 0, lt: 0, ltms: 0 }; scenes.set(s, r); }
  }
  return r;
}
/** Re-read the current scene label (cheap: the resolver returns interned strings). */
export function retag() {
  let s = 'other';
  try { s = resolveScene() || 'other'; } catch { /* never throws */ }
  if (s !== label || !rec) { label = s; rec = sceneRec(s); }
}

function noteHitch(ms, now) {
  let p = null;
  try { p = resolveSub(label); } catch { /* */ }
  const h = { ms: Math.round(ms), t: Math.round(now - chunkT0), s: label, p: p ? String(p).slice(0, 24) : null };
  const qa = window.__nmPerfLog;   // QA harness only (scripts/perf-journey.mjs): every hitch, page-clock stamped
  if (qa && qa.length < 5000) qa.push({ at: now, ms, s: label, p: h.p });
  if (worst.length < MAX_WORST) { worst.push(h); return; }
  let lo = 0;
  for (let i = 1; i < worst.length; i++) if (worst[i].ms < worst[lo].ms) lo = i;
  if (h.ms > worst[lo].ms) worst[lo] = h;
}

// ------------------------------------------------------------------ the frame loop
let last = 0, running = false;
function frame(now) {
  requestAnimationFrame(frame);
  retag();
  if (last) {
    const d = now - last;
    if (d > 0 && d < 30000) {
      const r = rec;
      r.n++; r.ms += d;
      const i = d < EDGES[0] ? 0 : d < EDGES[1] ? 1 : d < EDGES[2] ? 2 : d < EDGES[3] ? 3 : d < EDGES[4] ? 4 : 5;
      r.h[i]++;
      if (d > r.w) r.w = d;
      if (d >= HITCH_MS) { r.hx++; noteHitch(d, now); }
    }
  }
  last = now;
}

// ------------------------------------------------------------------ long frames
function srcOf(e) {
  const sc = e.scripts && e.scripts.length ? e.scripts.reduce((a, b) => (b.duration > a.duration ? b : a)) : null;
  if (!sc) return null;
  const file = String(sc.sourceURL || '').split('?')[0].split('/').pop();
  const fn = sc.sourceFunctionName || sc.invoker || '';
  const s = (file ? file + (fn ? ':' + fn : '') : fn).replace(/[^A-Za-z0-9._:@$/ <>-]/g, '').slice(0, 80);
  return s || null;
}
function observeLongFrames() {
  if (typeof PerformanceObserver !== 'function') return;
  const types = PerformanceObserver.supportedEntryTypes || [];
  const type = types.includes('long-animation-frame') ? 'long-animation-frame' : types.includes('longtask') ? 'longtask' : null;
  if (!type) return;
  try {
    new PerformanceObserver((list) => {
      if (!rec) retag();
      for (const e of list.getEntries()) {
        rec.lt++; rec.ltms += Math.round(e.duration);
        if (type !== 'long-animation-frame') continue;
        const ms = Math.round(e.duration);
        if (loafs.length >= MAX_LOAF && ms <= loafs[loafs.length - 1].ms) continue;
        let sl = e.styleAndLayoutStart ? Math.max(0, e.startTime + e.duration - e.styleAndLayoutStart) : 0;
        if (e.scripts) for (const sc of e.scripts) sl += sc.forcedStyleAndLayoutDuration || 0;
        let p = null;
        try { p = resolveSub(label); } catch { /* */ }
        loafs.push({ ms, blk: Math.round(e.blockingDuration || 0), sl: Math.round(sl), s: label, p: p ? String(p).slice(0, 24) : null, src: srcOf(e) });
        loafs.sort((a, b) => b.ms - a.ms);
        if (loafs.length > MAX_LOAF) loafs.length = MAX_LOAF;
      }
    }).observe({ type, buffered: false });
  } catch { /* unsupported */ }
}

// ------------------------------------------------------------------ device context
function family() {
  const ua = navigator.userAgent || '';
  const br = /SamsungBrowser/.test(ua) ? 'samsung' : /; wv\)/.test(ua) ? 'webview' : /Edg(A|iOS)?\//.test(ua) ? 'edge'
    : /FxiOS|Firefox\//.test(ua) ? 'firefox' : /CriOS|Chrome\//.test(ua) ? 'chrome' : /Safari\//.test(ua) ? 'safari' : 'other';
  const os = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) ? 'ios'
    : /Android/.test(ua) ? 'android' : /CrOS/.test(ua) ? 'chromeos' : /Mac OS X/.test(ua) ? 'mac'
    : /Windows/.test(ua) ? 'windows' : /Linux/.test(ua) ? 'linux' : 'other';
  return { br, os };
}
/** The GPU renderer string from a live GL context (WEBGL_debug_renderer_info where exposed). */
export function setGpuFrom(gl) {
  try {
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    const s = ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
    gpu = String(s || '').replace(/[^\x20-\x7e]/g, '').slice(0, 120) || null;
  } catch { gpu = null; }
}
function device() {
  const q = quality() || {};
  const f = family();
  return {
    gpu, dpr: +(devicePixelRatio || 1).toFixed(3), vw: innerWidth, vh: innerHeight,
    tier: q.step | 0, sheds: q.sheds | 0, restores: q.restores | 0,
    cores: navigator.hardwareConcurrency || null, mem: navigator.deviceMemory || null, br: f.br, os: f.os,
  };
}

// ------------------------------------------------------------------ sending
/** The current chunk as the beacon body (also the QA surface: window.__nm.perf()). */
export function snapshot(why = 'chunk') {
  const list = [...scenes.values()].filter(r => r.n > 0).map(r => ({ ...r, ms: Math.round(r.ms), w: Math.round(r.w), h: r.h.slice() }));
  return { v: 1, app, sid, seq, why, ms: Math.round(performance.now() - chunkT0), dev: device(), scenes: list,
    worst: worst.slice().sort((a, b) => b.ms - a.ms), loaf: loafs.slice() };
}
function reset() {
  scenes = new Map(); worst = []; loafs = []; chunkT0 = performance.now(); rec = null; retag();
}
export function flush(why) {
  try {
    let frames = 0;
    for (const r of scenes.values()) frames += r.n;
    if (frames < MIN_FRAMES) return false;
    const body = JSON.stringify(snapshot(why));
    seq++;
    reset();
    if (navigator.sendBeacon) navigator.sendBeacon(URL_BEACON, body);
    else fetch(URL_BEACON, { method: 'POST', body, keepalive: true }).catch(() => {});
    return true;
  } catch { return false; }
}
/** End of a match: send once the result screen has settled (idle, never mid-animation). */
export function matchOver() {
  const go = () => flush('match');
  if (typeof requestIdleCallback === 'function') requestIdleCallback(go, { timeout: 4000 });
  else setTimeout(go, 2500);
}

/**
 * opts: { app, scene: () => label, sub: (label) => sub-phase | null, quality: () => ({step, sheds, restores}) }
 * The scene resolver is read every frame; keep it allocation-free (return interned strings).
 */
export function start(opts = {}) {
  if (running) return;
  running = true;
  app = String(opts.app || app).slice(0, 12) + (navigator.webdriver ? '-qa' : '');
  if (opts.scene) resolveScene = opts.scene;
  if (opts.sub) resolveSub = opts.sub;
  if (opts.quality) quality = opts.quality;
  retag();
  observeLongFrames();
  document.addEventListener('visibilitychange', () => {
    last = 0;                     // the interval across a hidden stretch is not a frame
    if (document.hidden) flush('hide');
  });
  addEventListener('pagehide', () => flush('unload'));
  requestAnimationFrame(frame);
}
